// Time-zone helpers. Events store an IANA zone (e.g. "America/New_York");
// we derive the 3-letter abbreviation (EDT/EST/…) and convert venue wall-clock
// times to UTC so calendar exports land at the correct local time everywhere.
import api from "@/lib/api";

// IANA timezone for a US ZIP, via the backend (uses timezonefinder). Cached.
const ZIP_TZ_CACHE = new Map();
export async function timezoneForZip(zip) {
    if (!/^\d{5}$/.test(zip || "")) return null;
    if (ZIP_TZ_CACHE.has(zip)) return ZIP_TZ_CACHE.get(zip);
    try {
        const { data } = await api.get(`/geo/zip-timezone?zip=${zip}`);
        const tz = data?.timezone || null;
        ZIP_TZ_CACHE.set(zip, tz);
        return tz;
    } catch {
        return null;
    }
}

// Full zone name for a date (e.g. "Eastern Time"), stripping Daylight/Standard.
export function tzLongName(tz, ymd) {
    if (!tz) return "";
    try {
        const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(ymd || "").trim());
        const now = new Date();
        const [y, mo, d] = m ? [+m[1], +m[2], +m[3]] : [now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate()];
        const dt = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
        const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "long" }).formatToParts(dt);
        const p = parts.find((x) => x.type === "timeZoneName");
        return p ? p.value.replace(/ (Daylight|Standard)/, "") : "";
    } catch {
        return "";
    }
}

// 3-letter zone abbreviation for a given IANA zone on a given date (DST-aware).
export function tzAbbrev(tz, ymd) {
    if (!tz) return "";
    try {
        const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(ymd || "").trim());
        const now = new Date();
        const [y, mo, d] = m ? [+m[1], +m[2], +m[3]] : [now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate()];
        const dt = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0)); // noon avoids midnight DST edges
        const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(dt);
        const p = parts.find((x) => x.type === "timeZoneName");
        return p ? p.value : "";
    } catch {
        return "";
    }
}

// Offset (ms) between the given IANA zone and UTC at a specific instant.
function tzOffsetMs(tz, date) {
    const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone: tz, hourCycle: "h23",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const p = dtf.formatToParts(date).reduce((a, x) => { if (x.type !== "literal") a[x.type] = x.value; return a; }, {});
    const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return asUTC - date.getTime();
}

// Convert a wall-clock time (YYYY-MM-DD + minutes past midnight) in `tz` to a UTC Date.
export function wallToUtc(tz, ymd, minutes) {
    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(ymd || "").trim());
    if (!m) return null;
    const [y, mo, d] = [+m[1], +m[2], +m[3]];
    const h = Math.floor(minutes / 60), mi = minutes % 60;
    const guess = Date.UTC(y, mo - 1, d, h, mi, 0);
    let off = tzOffsetMs(tz, new Date(guess));
    off = tzOffsetMs(tz, new Date(guess - off)); // refine once for DST transitions
    return new Date(guess - off);
}
